<template>
  <div>
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';

interface DataTableInstance {
  loadData: () => void;
}

// Get i18n composable
const { t } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const dataTableRef = ref<DataTableInstance | null>(null);
const dynamicColumns = ref<string[]>([]);
const dataLoaded = ref<boolean>(false);

// Computed properties
const endpoint = computed(() => '/index.php/relatorios/performance_franquias/buscaDadosTabela');

const tableColumns = computed(() => {
  // Base columns that are always present
  const baseColumns = [
    {
      data: 'franquia_id',
      title: 'ID',
      width: 80,
      sortable: true
    },
    {
      data: 'franquia',
      title: t('Franquia'),
      width: 150,
      sortable: true
    },
    {
      data: 'total_periodo',
      title: t('Total no período'),
      width: 150,
      sortable: true,
      renderType: 'currency',
      footerType: 'sum'
    }
  ];

  // Add dynamic date columns
  const dateColumns = dynamicColumns.value.map(colName => ({
    data: colName,
    title: colName,
    width: 130,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  }));

  return [...baseColumns, ...dateColumns];
});

const dataTableConfig = computed(() => {
  return {
    tableId: 'tabelaPerformanceFranquias',
    filename: `performance_franquias_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 100,
    pagination: true,
    footerLabel: t('Totais'),
    rowSelection: 'single',
    resizableColumns: true,
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const filterConfig = computed(() => {
  const baseFilters = {
    'franquia_id': 'text',
    'franquia': 'text',
    'total_periodo': 'range'
  };

  // Add range filters for dynamic date columns
  const dateFilters: Record<string, string> = {};
  dynamicColumns.value.forEach(colName => {
    dateFilters[colName] = 'range';
  });

  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      ...baseFilters,
      ...dateFilters
    }
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

// Methods
const loadData = () => {
  const stack = new Error().stack;
  log('PerformanceFranquiasTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (dataTableRef.value) {
    dataTableRef.value.loadData();
  }
};

const extractDynamicColumns = (data: unknown[]): string[] => {
  if (data && data.length > 0) {
    const firstRow = data[0] as Record<string, unknown>;
    const columns = Object.keys(firstRow).filter(
      key => key !== 'franquia_id' && key !== 'franquia' && key !== 'total_periodo' && !key.endsWith('_display')
    );
    log('PerformanceFranquiasTable', 'extractDynamicColumns', 'Extracted dynamic columns', columns);
    return columns;
  }
  return [];
};

const handleDataLoaded = (data: unknown[]): void => {
  log('PerformanceFranquiasTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');

  const newDynamicColumns = extractDynamicColumns(data);

  if (JSON.stringify(newDynamicColumns) !== JSON.stringify(dynamicColumns.value)) {
    log('PerformanceFranquiasTable', 'handleDataLoaded', 'Updating dynamic columns');
    dynamicColumns.value = newDynamicColumns;
  }

  dataLoaded.value = true;
  // Sem toast de sucesso: a tabela aparecendo JA e o aviso de que carregou.
  // O toast nasce no canto superior e cobre o cabecalho das colunas de data —
  // e como a tela carregava duas vezes (ver onFilterUpdated), vinham dois
  // empilhados, tapando duas colunas inteiras (chamado 2026-0013).
  // Erro continua avisando: falha silenciosa e que nao pode acontecer.
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('PerformanceFranquiasTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : error.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar performance das franquias: ') + message);
};

/**
 * `filter.updated` dispara tambem quando o filtro global TERMINA DE INICIALIZAR
 * no boot do app — nao so quando a pessoa muda alguma coisa. Nesse instante o
 * DataTable ja carregou sozinho pelo autoLoad, entao recarregar era a segunda
 * busca dos mesmos 104 registros (chamado 2026-0013).
 *
 * Se o filtro ainda nao estava pronto quando montamos, o primeiro evento e a
 * inicializacao dele e nao uma mudanca — esse a gente ignora. Do segundo em
 * diante é a pessoa mexendo no filtro, e aí recarrega.
 */
const ignorarInicializacaoDoFiltro = ref(false);

const onFilterUpdated = (): void => {
  if (ignorarInicializacaoDoFiltro.value) {
    ignorarInicializacaoDoFiltro.value = false;
    log('PerformanceFranquiasTable', 'event:filter.updated', 'Ignorado: inicializacao do filtro; o autoLoad ja carregou');
    return;
  }
  log('PerformanceFranquiasTable', 'event:filter.updated', 'Received, reloading data');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('PerformanceFranquiasTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('PerformanceFranquiasTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  ignorarInicializacaoDoFiltro.value = !initialized.value;

  eventBus.on('filter.updated', onFilterUpdated);
});

onBeforeUnmount(() => {
  log('PerformanceFranquiasTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', onFilterUpdated);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
</style>
